import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { getClinicalPlans, getArchivedClinicalPlans, createClinicalPlan, updateClinicalPlan, deleteClinicalPlan, markClinicalPlanReviewed, getClinicalPlanReviewStatus, getStaff } from "../../lib/api";
import { useAuth } from "../../App";

// Standard staff roles used for role-based reviewers / visibility. Any extra
// positions found on the live staff list are merged in (see rolesFromStaff).
const STANDARD_ROLES = ["Admin", "Teacher", "Clinician", "Special Educator"];

const rolesFromStaff = (staff) => {
  const set = new Set(STANDARD_ROLES);
  (staff || []).forEach((s) => (s.positions || []).forEach((p) => p && set.add(p)));
  return Array.from(set);
};

// A small toggle "pill" used for selecting roles / staff.
function TogglePill({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? "bg-[#00305E] text-white border-[#00305E]"
          : "bg-white text-gray-600 border-gray-300 hover:border-[#00305E]"
      }`}
    >
      {children}
    </button>
  );
}

const getRiskLevelColor = (level) => {
  switch (level) {
    case "high": return "bg-red-100 border-red-300 text-red-800";
    case "medium": return "bg-yellow-100 border-yellow-300 text-yellow-800";
    case "low": return "bg-green-100 border-green-300 text-green-800";
    default: return "bg-gray-100 border-gray-300 text-gray-800";
  }
};

const getRiskBadgeColor = (level) => {
  switch (level) {
    case "high": return "bg-red-500 text-white";
    case "medium": return "bg-yellow-500 text-white";
    case "low": return "bg-green-500 text-white";
    default: return "bg-gray-500 text-white";
  }
};

const isReviewOverdue = (reviewDate) => {
  if (!reviewDate) return false;
  return new Date(reviewDate) < new Date();
};

const isReviewSoon = (reviewDate) => {
  if (!reviewDate) return false;
  const review = new Date(reviewDate);
  const today = new Date();
  const weekFromNow = new Date();
  weekFromNow.setDate(today.getDate() + 7);
  return review >= today && review <= weekFromNow;
};

const defaultFormData = {
  plan_interventions: "",
  goal: "",
  staff_responsibilities: "",
  risk_level: "medium",
  review_date: "",
  review_roles: [],
  review_staff_ids: [],
  visibility_all_staff: true,
  visibility_roles: [],
};

// Admin-only panel: who has and hasn't reviewed each notification event for a plan.
function ReviewStatusPanel({ planId }) {
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getClinicalPlanReviewStatus(planId);
      setEvents(res.data.events || []);
    } catch (err) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && events === null) load();
  };

  return (
    <div className="mt-3 pt-3 border-t" data-testid={`review-tracking-${planId}`}>
      <button
        onClick={toggle}
        className="flex items-center gap-2 text-xs font-semibold text-[#00305E] hover:underline"
        data-testid={`review-tracking-toggle-${planId}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
        Staff Review Tracking
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? "rotate-180" : ""}`}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {loading && <p className="text-xs text-gray-500">Loading…</p>}
          {!loading && events && events.length === 0 && (
            <p className="text-xs text-gray-500">No review notifications were sent for this plan yet.</p>
          )}
          {!loading && events && events.map((ev) => (
            <div key={ev.event_id} className="rounded-md bg-white/70 border p-3 text-xs" data-testid={`review-event-${ev.event_id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-700">
                  {ev.event_type === "created" ? "Plan created" : "Plan updated"} by {ev.changed_by_name || "staff"}
                  {ev.created_at ? ` · ${new Date(ev.created_at).toLocaleDateString()}` : ""}
                </span>
                <span className={`px-2 py-0.5 rounded-full font-semibold ${ev.reviewed_count === ev.total ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                  {ev.reviewed_count}/{ev.total} reviewed
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="font-semibold text-green-700 mb-1">Reviewed ({ev.reviewed.length})</p>
                  {ev.reviewed.length === 0 ? <p className="text-gray-400">None yet</p> : (
                    <ul className="space-y-0.5">
                      {ev.reviewed.map((r) => (
                        <li key={r.staff_id} className="text-gray-700">
                          {r.name}
                          {r.reviewed_at ? <span className="text-gray-400"> · {new Date(r.reviewed_at).toLocaleDateString()}</span> : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-amber-700 mb-1">Not yet reviewed ({ev.pending.length})</p>
                  {ev.pending.length === 0 ? <p className="text-gray-400">Everyone reviewed</p> : (
                    <ul className="space-y-0.5">
                      {ev.pending.map((r) => (
                        <li key={r.staff_id} className="text-gray-700">{r.name}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClinicalPlansTab({ clinicalPlans, setClinicalPlans, studentId }) {
  const { isAdmin } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [staff, setStaff] = useState([]);
  const [showPrevious, setShowPrevious] = useState(false);
  const [archivedPlans, setArchivedPlans] = useState([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);
  const [loadingArchived, setLoadingArchived] = useState(false);

  useEffect(() => {
    getStaff().then((res) => setStaff(res.data || [])).catch(() => setStaff([]));
  }, []);

  const availableRoles = rolesFromStaff(staff);
  const sortedStaff = [...staff].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const staffNameById = staff.reduce((acc, s) => ({ ...acc, [s.id]: s.name }), {});

  // Only the CURRENT plan (most-recently-updated active) is shown by default; a
  // toggle reveals non-current plans (older active + archived).
  const sortedPlans = [...(clinicalPlans || [])].sort(
    (a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || "")
  );
  const currentPlan = sortedPlans[0] || null;
  const olderActive = sortedPlans.slice(1);
  const previousPlans = [...olderActive, ...archivedPlans];
  const visiblePlans = showPrevious
    ? [currentPlan, ...previousPlans].filter(Boolean)
    : (currentPlan ? [currentPlan] : []);

  const togglePrevious = async () => {
    const next = !showPrevious;
    setShowPrevious(next);
    if (next && !archivedLoaded) {
      setLoadingArchived(true);
      try {
        const res = await getArchivedClinicalPlans(studentId);
        setArchivedPlans(res.data || []);
      } catch (err) {
        setArchivedPlans([]);
      } finally {
        setArchivedLoaded(true);
        setLoadingArchived(false);
      }
    }
  };

  const toggleInArray = (field, value) => {
    setFormData((p) => {
      const cur = p[field] || [];
      return { ...p, [field]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  };

  // Opening/viewing the Clinical Plans tab counts as "reviewing" the plan — mark
  // the current user's pending review notifications for these plans as reviewed.
  useEffect(() => {
    (clinicalPlans || []).forEach((p) => {
      markClinicalPlanReviewed(p.id).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(clinicalPlans || []).map((p) => p.id).join(",")]);

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        plan_interventions: plan.plan_interventions || "",
        goal: plan.goal || "",
        staff_responsibilities: plan.staff_responsibilities || "",
        risk_level: plan.risk_level || "medium",
        review_date: plan.review_date || "",
        review_roles: plan.review_roles || [],
        review_staff_ids: plan.review_staff_ids || [],
        visibility_all_staff: plan.visibility_all_staff !== false,
        visibility_roles: plan.visibility_roles || [],
      });
    } else {
      setEditingPlan(null);
      setFormData({ ...defaultFormData });
    }
    setDialogOpen(true);
  };

  const handleSavePlan = async () => {
    if (!formData.plan_interventions.trim()) {
      toast.error("Please enter the plan/interventions");
      return;
    }
    try {
      if (editingPlan) {
        await updateClinicalPlan(editingPlan.id, formData);
        toast.success("Clinical plan updated");
      } else {
        const res = await createClinicalPlan({ ...formData, student_id: studentId });
        toast.success(res?.data?.merged ? "Added to current clinical plan" : "Clinical plan created");
      }
      setDialogOpen(false);
      const plansRes = await getClinicalPlans(studentId);
      setClinicalPlans(plansRes.data);
    } catch (err) {
      toast.error("Failed to save clinical plan");
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this clinical plan?")) return;
    try {
      await deleteClinicalPlan(planId);
      toast.success("Clinical plan deleted");
      const plansRes = await getClinicalPlans(studentId);
      setClinicalPlans(plansRes.data);
    } catch (err) {
      toast.error("Failed to delete clinical plan");
    }
  };

  const handleArchivePlan = async (planId) => {
    try {
      await updateClinicalPlan(planId, { status: "archived" });
      toast.success("Clinical plan archived");
      const plansRes = await getClinicalPlans(studentId);
      setClinicalPlans(plansRes.data);
    } catch (err) {
      toast.error("Failed to archive clinical plan");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Clinical Plans</CardTitle>
              <CardDescription>Track interventions, goals, and staff responsibilities</CardDescription>
            </div>
            <Button onClick={() => handleOpenDialog()} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="add-clinical-plan-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Add Clinical Plan
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {currentPlan ? (
            <div className="space-y-4">
              {visiblePlans.map((plan) => {
                const isPrevious = plan.id !== currentPlan?.id;
                return (
                <div 
                  key={plan.id} 
                  className={`border-2 rounded-lg p-4 ${getRiskLevelColor(plan.risk_level)} ${isPrevious ? "opacity-80" : ""}`}
                  data-testid={`clinical-plan-${plan.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {isPrevious && (
                        <span className="px-2 py-1 rounded text-xs font-semibold uppercase bg-gray-500 text-white" data-testid={`plan-previous-badge-${plan.id}`}>
                          {plan.status === "archived" ? "Archived" : "Previous"}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${getRiskBadgeColor(plan.risk_level)}`}>
                        {plan.risk_level} Risk
                      </span>
                      {plan.review_date && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          isReviewOverdue(plan.review_date) 
                            ? 'bg-red-600 text-white' 
                            : isReviewSoon(plan.review_date) 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-gray-200 text-gray-700'
                        }`}>
                          {isReviewOverdue(plan.review_date) 
                            ? `Review OVERDUE` 
                            : isReviewSoon(plan.review_date) 
                              ? `Review Soon` 
                              : `Review: ${new Date(plan.review_date).toLocaleDateString()}`
                          }
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(plan)} data-testid={`edit-plan-${plan.id}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                        </svg>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleArchivePlan(plan.id)} title="Archive plan">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/>
                        </svg>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeletePlan(plan.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-plan-${plan.id}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                        </svg>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs font-semibold uppercase text-gray-600">Plan / Interventions</Label>
                      <p className="text-sm mt-1 whitespace-pre-wrap">{plan.plan_interventions}</p>
                    </div>
                    {plan.goal && (
                      <div>
                        <Label className="text-xs font-semibold uppercase text-gray-600">Goal</Label>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{plan.goal}</p>
                      </div>
                    )}
                    {plan.staff_responsibilities && (
                      <div>
                        <Label className="text-xs font-semibold uppercase text-gray-600">Staff Responsibilities</Label>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{plan.staff_responsibilities}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t">
                      <span>Created: {new Date(plan.created_at).toLocaleDateString()}</span>
                      {plan.updated_at && plan.updated_at !== plan.created_at && (
                        <span>Updated: {new Date(plan.updated_at).toLocaleDateString()}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600" data-testid={`plan-access-summary-${plan.id}`}>
                      <span>
                        <span className="font-semibold">Visible to:</span>{" "}
                        {plan.visibility_all_staff === false
                          ? `Creator, Admins${(plan.visibility_roles || []).length ? ", " + plan.visibility_roles.join(", ") : ""}${((plan.review_roles || []).length || (plan.review_staff_ids || []).length) ? " + reviewers" : ""}`
                          : "All staff"}
                      </span>
                      <span>
                        <span className="font-semibold">Reviewers:</span>{" "}
                        {((plan.review_roles || []).length || (plan.review_staff_ids || []).length)
                          ? [
                              ...(plan.review_roles || []),
                              ...(plan.review_staff_ids || []).map((id) => staffNameById[id] || "Staff"),
                            ].join(", ")
                          : "All staff"}
                      </span>
                    </div>
                    {isAdmin() && <ReviewStatusPanel planId={plan.id} />}
                  </div>
                </div>
                );
              })}

              {previousPlans.length > 0 || !archivedLoaded ? (
                <button
                  onClick={togglePrevious}
                  className="flex items-center gap-2 text-sm font-semibold text-[#00305E] hover:underline"
                  data-testid="toggle-previous-plans"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${showPrevious ? "rotate-180" : ""}`}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                  {loadingArchived
                    ? "Loading previous plans…"
                    : showPrevious
                      ? "Hide previous plans"
                      : `Show previous plans${archivedLoaded ? ` (${previousPlans.length})` : ""}`}
                </button>
              ) : (
                <p className="text-sm text-gray-400" data-testid="no-previous-plans">No previous plans.</p>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-gray-300">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
              </svg>
              <p className="text-muted-foreground">No clinical plans yet</p>
              <p className="text-sm text-gray-400 mt-1">Add a clinical plan to track interventions and goals</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clinical Plan Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? "Edit Clinical Plan" : "Add Clinical Plan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Risk Level *</Label>
                <Select value={formData.risk_level} onValueChange={(v) => setFormData(p => ({ ...p, risk_level: v }))}>
                  <SelectTrigger data-testid="plan-risk-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        High Risk
                      </span>
                    </SelectItem>
                    <SelectItem value="medium">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                        Medium Risk
                      </span>
                    </SelectItem>
                    <SelectItem value="low">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        Low Risk
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Review Date</Label>
                <Input
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData(p => ({ ...p, review_date: e.target.value }))}
                  data-testid="plan-review-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Plan / Interventions *</Label>
              <Textarea
                value={formData.plan_interventions}
                onChange={(e) => setFormData(p => ({ ...p, plan_interventions: e.target.value }))}
                placeholder="Describe the clinical plan, interventions, check-in procedures..."
                rows={5}
                data-testid="plan-interventions-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Goal</Label>
              <Textarea
                value={formData.goal}
                onChange={(e) => setFormData(p => ({ ...p, goal: e.target.value }))}
                placeholder="What is the goal for this student?"
                rows={3}
                data-testid="plan-goal-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Staff Responsibilities</Label>
              <Textarea
                value={formData.staff_responsibilities}
                onChange={(e) => setFormData(p => ({ ...p, staff_responsibilities: e.target.value }))}
                placeholder="What are staff members responsible for?"
                rows={3}
                data-testid="plan-staff-responsibilities-input"
              />
            </div>

            {/* Who needs to review this plan */}
            <div className="space-y-2 border-t pt-4" data-testid="plan-reviewers-section">
              <Label>Who needs to review this plan?</Label>
              <p className="text-xs text-gray-500">
                Selected people get a review notification they must open before they can dismiss it.
                Leave everything unchecked to require <strong>all staff</strong> (default).
              </p>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">By role</p>
                <div className="flex flex-wrap gap-2">
                  {availableRoles.map((r) => (
                    <TogglePill
                      key={r}
                      active={(formData.review_roles || []).includes(r)}
                      onClick={() => toggleInArray("review_roles", r)}
                      testId={`reviewer-role-${r.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {r}
                    </TogglePill>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1 mt-2">Specific people</p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {sortedStaff.map((s) => (
                    <TogglePill
                      key={s.id}
                      active={(formData.review_staff_ids || []).includes(s.id)}
                      onClick={() => toggleInArray("review_staff_ids", s.id)}
                      testId={`reviewer-staff-${s.id}`}
                    >
                      {s.name}
                    </TogglePill>
                  ))}
                </div>
              </div>
            </div>

            {/* Who can see this plan */}
            <div className="space-y-2 border-t pt-4" data-testid="plan-visibility-section">
              <Label>Who can see this plan?</Label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.visibility_all_staff}
                  onChange={(e) => setFormData(p => ({ ...p, visibility_all_staff: e.target.checked }))}
                  data-testid="visibility-all-staff-toggle"
                  className="h-4 w-4"
                />
                Visible to all staff
              </label>
              {!formData.visibility_all_staff && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">
                    You (the creator) and admins can always see it. Anyone required to review can always see it too.
                    Pick any additional roles that should have access:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableRoles.map((r) => (
                      <TogglePill
                        key={r}
                        active={(formData.visibility_roles || []).includes(r)}
                        onClick={() => toggleInArray("visibility_roles", r)}
                        testId={`visibility-role-${r.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {r}
                      </TogglePill>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePlan} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-plan-btn">
                {editingPlan ? "Save Changes" : "Create Plan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
